import React, { useMemo } from "react";
import { IconChevronDown, IconMaximize } from "@tabler/icons-react";
import {
  useEncoderAhaByIds,
  useEncoderIcdGuidelinesByIds,
} from "../../hooks/queries/useEncoder";
import type { AhaGuideline, IcdGuideline } from "../../types/encoder";
import {
  cn,
  formatAhaGuidelineTitle,
  formatAhaYearQuarterPage,
  getAhaExpandedQaContent,
  sanitizeGuidelineText,
} from "../../utils/encoder";
import {
  type CmReferenceKeys,
  cmGuidelineTitle,
  cmGuidelineTrail,
  highlightCmMatches,
} from "../../utils/encoder-cm";
import { Spinner } from "./shared";

export type CmReferenceTab = "guidelines" | "aha";

export type CmCodingReferenceData = {
  tab: CmReferenceTab;
  onTabChange: (tab: CmReferenceTab) => void;
  keys: CmReferenceKeys | null;
  description: string;
  guidelines: IcdGuideline[];
  guidelinesLoading: boolean;
  guidelinesError: boolean;
  aha: AhaGuideline[];
  ahaLoading: boolean;
  ahaError: boolean;
  openGuidelineIds: number[];
  onToggleGuideline: (id: number) => void;
  openAhaIds: number[];
  onToggleAha: (id: number) => void;
};

const TAB_ITEMS: Array<{ value: CmReferenceTab; label: string }> = [
  { value: "guidelines", label: "Coding Guidelines" },
  { value: "aha", label: "AHA Coding Clinic" },
];

const tabTriggerClass =
  "relative flex items-center justify-center gap-1.5 rounded-t-sm border-b-2 border-transparent py-1 whitespace-nowrap transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-green-100 hover:text-green-700";
const tabActiveClass =
  "z-20 border-green-700 bg-green-600 font-semibold text-white shadow-lg hover:bg-green-600 hover:text-white";

const ReferenceTabs: React.FC<
  Pick<
    CmCodingReferenceData,
    "tab" | "onTabChange" | "guidelines" | "aha"
  > & { size?: "compact" | "full" }
> = ({ tab, onTabChange, guidelines, aha, size = "compact" }) => {
  const counts: Record<CmReferenceTab, number> = {
    guidelines: guidelines.length,
    aha: aha.length,
  };
  return (
    <div className="flex shrink-0 flex-nowrap items-stretch justify-start gap-1.5">
      {TAB_ITEMS.map((item) => {
        const isActive = tab === item.value;
        const count = counts[item.value];
        return (
          <button
            key={item.value}
            type="button"
            onClick={() => onTabChange(item.value)}
            className={cn(
              tabTriggerClass,
              size === "full" ? "h-9 px-3.5 text-sm" : "h-8 px-2.5 text-xs",
              isActive && tabActiveClass
            )}
          >
            <span>{item.label}</span>
            {count > 0 ? (
              <span
                className={cn(
                  "rounded-full bg-blue-500 font-semibold leading-none text-white",
                  size === "full"
                    ? "px-2 py-0.5 text-[11px]"
                    : "px-1.5 py-0.5 text-[10px]"
                )}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

const HighlightedKeys: React.FC<{
  text: string;
  keys: CmReferenceKeys | null;
}> = ({ text, keys }) => {
  const words = keys ? [keys.code, keys.category] : [];
  const segments = highlightCmMatches(text, words);
  if (segments.length <= 1) return <>{text}</>;
  return (
    <>
      {segments.map((segment, index) =>
        segment.match ? (
          <mark
            key={`${segment.text}-${index}`}
            className="rounded-sm bg-yellow-100 px-0.5 font-semibold"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        )
      )}
    </>
  );
};

const ReferenceRow: React.FC<{
  index: number;
  title: string;
  meta: string;
  isOpen: boolean;
  onToggle: () => void;
  size: "compact" | "full";
  children: React.ReactNode;
}> = ({ index, title, meta, isOpen, onToggle, size, children }) => (
  <li className={cn(isOpen && "bg-slate-100/60")}>
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex w-full items-start gap-2 text-left transition-colors hover:bg-slate-100/50",
        size === "full" ? "px-5 py-3" : "px-3 py-2"
      )}
    >
      <span className="mt-px w-4 shrink-0 text-right text-xs text-slate-600">
        {index + 1}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block font-semibold text-slate-900",
            size === "full" ? "text-[15px] leading-6" : "text-[13px] leading-5"
          )}
        >
          {title}
        </span>
        {meta ? (
          <span className="mt-0.5 block text-xs text-slate-600">{meta}</span>
        ) : null}
      </span>
      <IconChevronDown
        size={16}
        className={cn(
          "mt-0.5 shrink-0 text-slate-500 transition-transform",
          isOpen && "rotate-180"
        )}
      />
    </button>
    {isOpen ? (
      <div
        className={cn(
          "space-y-2 border-l-2 border-l-slate-400 bg-white/60 text-slate-800",
          size === "full" ? "mx-5 mb-3 px-4 py-3 text-sm" : "mx-3 mb-2 px-3 py-2 text-xs"
        )}
      >
        {children}
      </div>
    ) : null}
  </li>
);

const ReferenceBody: React.FC<
  CmCodingReferenceData & { size?: "compact" | "full" }
> = ({
  tab,
  keys,
  guidelines,
  guidelinesLoading,
  guidelinesError,
  aha,
  ahaLoading,
  ahaError,
  openGuidelineIds,
  onToggleGuideline,
  openAhaIds,
  onToggleAha,
  size = "compact",
}) => {
  const guidelineDetails = useEncoderIcdGuidelinesByIds(
    openGuidelineIds,
    tab === "guidelines" && openGuidelineIds.length > 0
  );
  const ahaDetails = useEncoderAhaByIds(
    openAhaIds,
    tab === "aha" && openAhaIds.length > 0
  );

  const guidelineById = useMemo(() => {
    const map: Record<number, IcdGuideline> = {};
    const rows = guidelineDetails.data ?? [];
    for (let i = 0; i < rows.length; i += 1) map[rows[i].id] = rows[i];
    return map;
  }, [guidelineDetails.data]);

  const ahaById = useMemo(() => {
    const map: Record<number, AhaGuideline> = {};
    const rows = ahaDetails.data ?? [];
    for (let i = 0; i < rows.length; i += 1) map[rows[i].id] = rows[i];
    return map;
  }, [ahaDetails.data]);

  if (!keys) {
    return (
      <p className="px-4 py-6 text-sm text-slate-600">
        Open a code in Tabular Validation to see the guidance published for it.
      </p>
    );
  }

  if (tab === "aha") {
    if (ahaLoading && aha.length === 0) {
      return <Spinner label="Looking up AHA Coding Clinic…" />;
    }
    if (ahaError) {
      return (
        <p className="px-4 py-6 text-sm text-red-700">
          Could not load AHA Coding Clinic references.
        </p>
      );
    }
    if (aha.length === 0) {
      return (
        <p className="px-4 py-6 text-sm text-slate-600">
          No AHA Coding Clinic reference mentions {keys.code} or category{" "}
          {keys.category}.
        </p>
      );
    }
    return (
      <ul className="divide-y">
        {aha.map((row, index) => {
          const isOpen = openAhaIds.indexOf(row.id) >= 0;
          const detail = ahaById[row.id] ?? (isOpen ? undefined : row);
          const { qaPairs, fallbackText } = detail
            ? getAhaExpandedQaContent(detail)
            : { qaPairs: [], fallbackText: "" };
          return (
            <ReferenceRow
              key={row.id}
              index={index}
              title={formatAhaGuidelineTitle(row)}
              meta={formatAhaYearQuarterPage(row)}
              isOpen={isOpen}
              onToggle={() => onToggleAha(row.id)}
              size={size}
            >
              {ahaDetails.isFetching && !detail ? (
                <p className="text-slate-600">Loading clinic text…</p>
              ) : (
                <>
                  {qaPairs.map((pair, pairIndex) => (
                    <div key={`${row.id}-qa-${pairIndex}`} className="space-y-2">
                      {pair.question ? (
                        <p>
                          <span className="font-semibold text-red-800">
                            Question:{" "}
                          </span>
                          {pair.question}
                        </p>
                      ) : null}
                      {pair.answer ? (
                        <p>
                          <span className="font-semibold text-green-800">
                            Answer:{" "}
                          </span>
                          {pair.answer}
                        </p>
                      ) : null}
                    </div>
                  ))}
                  {!qaPairs.length && fallbackText ? (
                    <p className="whitespace-pre-line">{fallbackText}</p>
                  ) : null}
                  {!qaPairs.length && !fallbackText ? (
                    <p className="text-slate-600">
                      No AHA Coding Clinic text is stored for this entry.
                    </p>
                  ) : null}
                </>
              )}
            </ReferenceRow>
          );
        })}
      </ul>
    );
  }

  if (guidelinesLoading && guidelines.length === 0) {
    return <Spinner label="Looking up official guidelines…" />;
  }
  if (guidelinesError) {
    return (
      <p className="px-4 py-6 text-sm text-red-700">
        Could not load the official ICD-10-CM guidelines.
      </p>
    );
  }
  if (guidelines.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-slate-600">
        No official guideline text is indexed for {keys.code} or category{" "}
        {keys.category}.
      </p>
    );
  }

  return (
    <ul className="divide-y">
      {guidelines.map((guideline, index) => {
        const isOpen = openGuidelineIds.indexOf(guideline.id) >= 0;
        const detail = guidelineById[guideline.id];
        const trail = cmGuidelineTrail(guideline);
        const codingNote = sanitizeGuidelineText(
          detail?.codingNote ?? guideline.codingNote ?? ""
        );
        return (
          <ReferenceRow
            key={guideline.id}
            index={index}
            title={cmGuidelineTitle(guideline)}
            meta={[trail, guideline.fyear ? `FY ${guideline.fyear}` : ""]
              .filter(Boolean)
              .join(" · ")}
            isOpen={isOpen}
            onToggle={() => onToggleGuideline(guideline.id)}
            size={size}
          >
            {guidelineDetails.isFetching && isOpen && !detail ? (
              <p className="text-slate-600">Loading guideline text…</p>
            ) : codingNote.trim() ? (
              <p className="whitespace-pre-line">
                <HighlightedKeys text={codingNote.trim()} keys={keys} />
              </p>
            ) : (
              <p className="text-slate-600">
                No guideline text is stored for this entry.
              </p>
            )}
          </ReferenceRow>
        );
      })}
    </ul>
  );
};

export const CmCodingReferencePanel: React.FC<
  CmCodingReferenceData & { onExpand: () => void }
> = ({ onExpand, ...props }) => (
  <footer className="flex shrink-0 items-center gap-1.5 border-t border-blue-50 bg-gray-300 p-1.5">
    <button
      type="button"
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-700 hover:bg-gray-200 hover:text-slate-900"
      aria-label={
        props.tab === "aha"
          ? "Expand AHA Coding Clinic"
          : "Expand coding guidelines"
      }
      title="Expand"
      onClick={onExpand}
    >
      <IconMaximize size={14} />
    </button>
    <ReferenceTabs
      {...props}
      size="compact"
      onTabChange={(tab) => {
        props.onTabChange(tab);
        onExpand();
      }}
    />
  </footer>
);

export const CmCodingReferenceDialog: React.FC<
  CmCodingReferenceData & { open: boolean; onClose: () => void }
> = ({ open, onClose, ...props }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[92vh] w-[min(96vw,80rem)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b px-6 py-3">
          <h2 className="min-w-0 flex-1 text-left font-semibold leading-snug text-green-800">
            <span className="font-mono text-xl tracking-wide">
              {props.keys?.code ?? "ICD-10-CM"}
            </span>
            {props.description ? (
              <span className="ml-2 text-sm font-medium text-green-700">
                - {props.description}
              </span>
            ) : null}
          </h2>
          <div className="flex shrink-0 items-center gap-2">
            <ReferenceTabs {...props} size="full" />
            <button
              type="button"
              className="h-8 shrink-0 rounded-md border border-slate-300 px-3 text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          <ReferenceBody {...props} size="full" />
        </div>
      </div>
    </div>
  );
};
